<!-- src/components/DataCounter.vue -->
<template>
  <div class="data-counter">
    <div class="hexagon">
      <span class="label">{{ label }}</span>
      <div class="number-container">
        <span class="number" ref="numberRef">{{ currentNumber }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, ref, onMounted } from 'vue'

const props = defineProps({
  label: String,
  target: Number,
  duration: {
    type: Number,
    default: 2000
  }
})

const currentNumber = ref(0)
const numberRef = ref(null)

const animateNumber = () => {
  const start = 0
  const end = props.target
  const startTime = performance.now()
  const duration = props.duration

  const step = (currentTime) => {
    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / duration, 1)
    const easeOutQuad = 1 - Math.pow(1 - progress, 3)
    const value = Math.floor(start + (end - start) * easeOutQuad)

    currentNumber.value = value

    if (progress < 1) {
      requestAnimationFrame(step)
    }
  }

  requestAnimationFrame(step)
}

onMounted(() => {
  animateNumber()
})
</script>

<style scoped>
.data-counter {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 1rem 0;
  position: relative;
  /* 确保在小屏幕上不会超出容器 */
  max-width: 100%;
  padding: 0 0.5rem;
  box-sizing: border-box;
}

.hexagon {
  position: relative;
  width: 180px;
  height: 160px;
  background: rgba(255, 255, 255, 0.8);
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(37, 99, 235, 0.3);
  box-shadow: 0 0 20px rgba(37, 99, 235, 0.1);
  transition: all 0.3s ease;
  /* 确保内容不会溢出六边形 */
  box-sizing: border-box;
}

.hexagon:hover {
  transform: translateY(-10px);
  box-shadow: 0 0 30px rgba(37, 99, 235, 0.2);
  border: 1px solid rgba(37, 99, 235, 0.5);
}

.hexagon::before {
  content: "";
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(45deg, #3b82f6, #60a5fa, #3b82f6);
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  z-index: -1;
  animation: rotate 3s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.label {
  font-size: 0.875rem;
  color: #374151;
  margin-bottom: 0.5rem;
  text-align: center;
  font-weight: 500;
  /* 防止文字溢出 */
  word-wrap: break-word;
  max-width: 90%;
}

.number-container {
  font-size: 2rem;
  font-weight: bold;
  color: #111827;
  position: relative;
}

.number {
  font-family: 'Courier New', monospace;
  background: linear-gradient(to right, #1e40af, #3b82f6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
}

/* 平板设备适配 */
@media (max-width: 768px) {
  .hexagon {
    width: 140px;
    height: 130px;
    padding: 15px;
  }
  
  .label {
    font-size: 0.75rem;
    margin-bottom: 0.25rem;
  }
  
  .number-container {
    font-size: 1.5rem;
  }
}

/* 手机设备适配 */
@media (max-width: 480px) {
  .hexagon {
    width: 110px;
    height: 100px;
    padding: 10px;
  }
  
  .label {
    font-size: 0.7rem;
    margin-bottom: 0.2rem;
  }
  
  .number-container {
    font-size: 1.25rem;
  }
  
  .data-counter {
    margin: 0.75rem 0;
  }
}

/* 小屏手机适配 */
@media (max-width: 360px) {
  .hexagon {
    width: 90px;
    height: 85px;
  }
  
  .label {
    font-size: 0.65rem;
  }
  
  .number-container {
    font-size: 1.1rem;
  }
}
</style>
    